import { GetUbicacionByIdUseCase } from "@/application/use-cases/ubicacion/GetUbicacionUseCase";
import { GetUbicacionesUseCase } from "@/application/use-cases/ubicacion/GetUbicacionesUseCase";
import { UbicacionParamsDTO } from "@/application/dto/ubicaciones/UbicacionParamsDTO";
import { UpdateUbicacionUseCase } from "@/application/use-cases/ubicacion/PutUbicacionUseCase";
import { container } from "@/di";
import { IUbicacionRepository } from "@/domain";
import { Ubicacion } from "@/domain/entities/ubicacion";
import { useCallback, useEffect, useState } from "react";

export function useUbicacion(id?: string) {
  const [ubicacion, setUbicacion] = useState<Ubicacion | null>(null);
  const [data, setData] = useState<Ubicacion[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadById = useCallback(async (id: number | string) => {
    try {
      setError(null);

      const result = await container
        .get<GetUbicacionByIdUseCase>("GetUbicacionByIdUseCase")
        .execute(String(id));

      setUbicacion(result);
      return result;
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "Error al cargar la ubicación";

      setError(message);
      throw err;
    }
  }, []);

  const create = useCallback(async (data: Omit<Ubicacion, "id">) => {
    return container
      .get<IUbicacionRepository>("IUbicacionRepository")
      .create(data);
  }, []);

  const loadAll = useCallback(async (params?: UbicacionParamsDTO) => {
    try {
      setLoading(true);
      setError(null);

      const result = await container
        .get<GetUbicacionesUseCase>("GetUbicacionesUseCase")
        .execute(params);

      setData(result);
      return result;
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "Error al cargar las ubicaciones";

      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const update = useCallback(async (
    id: string,
    data: Omit<Ubicacion, "id">
  ) => {
    try {
      setError(null);

      const result = await container
        .get<UpdateUbicacionUseCase>("UpdateUbicacionUseCase")
        .execute(id, data);

      setUbicacion(result);
      setData((current) => current.map((item) =>
        String(item.id) === id ? result : item
      ));
      return result;
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "Error al actualizar la ubicación";

      setError(message);
      throw err;
    }
  }, []);

  useEffect(() => {
    if (id) {
      loadById(id);
    }
  }, [id, loadById]);

  return {
    data: ubicacion,
    ubicaciones: data,
    loading,
    error,
    reload: loadById,
    loadById,
    loadAll,
    create,
    update,
  };
}

